/**
 * useDetailDensity — shared hook for detail page density classes.
 *
 * Reads detailDensity + detailTextSize from Zustand preferencesStore,
 * applies mobile override (< 768px → always "default"), and returns
 * concrete Tailwind class strings via Record maps.
 *
 * Any Shopify-style widget can import this hook instead of threading
 * density props through 5 layers.
 */

import { usePreferencesStore, type DetailDensity, type DetailTextSize } from "@/lib/store/preferencesStore";
import { useMediaQuery } from "@/hooks/useMediaQuery";

// ── Density class maps ──────────────────────────────────────

/** Gap between section cards */
export const SECTION_GAP: Record<DetailDensity, string> = {
  compact: "space-y-3",
  default: "space-y-6",
  spacious: "space-y-8",
};

/** Card outer padding (grid-style sections) */
export const CARD_PADDING: Record<DetailDensity, string> = {
  compact: "px-4 py-4",
  default: "px-6 py-8",
  spacious: "px-8 py-10",
};

/** Inner vertical gap within a card */
export const CARD_INNER_GAP: Record<DetailDensity, string> = {
  compact: "space-y-2",
  default: "space-y-4",
  spacious: "space-y-5",
};

/** Grid gap between columns */
export const GRID_GAP: Record<DetailDensity, string> = {
  compact: "gap-2",
  default: "gap-4",
  spacious: "gap-5",
};

/** Vertical gap within a single column */
export const COL_GAP: Record<DetailDensity, string> = {
  compact: "space-y-3",
  default: "space-y-6",
  spacious: "space-y-8",
};

/** Apple row horizontal padding */
export const APPLE_ROW_PX: Record<DetailDensity, string> = {
  compact: "px-3",
  default: "px-4",
  spacious: "px-5",
};

/** Apple row vertical padding (non-first rows) */
export const APPLE_ROW_PY: Record<DetailDensity, string> = {
  compact: "py-2",
  default: "py-5",
  spacious: "py-6",
};

/** Apple row first-row padding */
export const APPLE_ROW_FIRST: Record<DetailDensity, string> = {
  compact: "pt-2.5 pb-2",
  default: "pt-5 pb-4",
  spacious: "pt-6 pb-5",
};

/** Divider horizontal margin */
export const APPLE_DIVIDER_MX: Record<DetailDensity, string> = {
  compact: "mx-3",
  default: "mx-4",
  spacious: "mx-5",
};

/** Apple row gap between label and value */
export const APPLE_ROW_GAP: Record<DetailDensity, string> = {
  compact: "gap-2",
  default: "gap-4",
  spacious: "gap-5",
};

// ── Text size maps ──────────────────────────────────────────

/** Field label + value text size */
export const TEXT_LABEL: Record<DetailTextSize, string> = {
  small: "text-xs",
  medium: "text-sm",
  large: "text-[15px]",
};

/** Apple-style key-value row label size (larger than section header) */
export const TEXT_ROW_LABEL: Record<DetailTextSize, string> = {
  small: "text-sm",
  medium: "text-lg",
  large: "text-xl",
};

/** Section header text size */
export const TEXT_SECTION_HEADER: Record<DetailTextSize, string> = {
  small: "text-[11px]",
  medium: "text-xs",
  large: "text-sm",
};

// ── Hook ────────────────────────────────────────────────────

export function useDetailDensity() {
  const detailDensity = usePreferencesStore((s) => s.detailDensity);
  const detailTextSize = usePreferencesStore((s) => s.detailTextSize);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const density = isDesktop ? detailDensity : "default";

  return {
    density,
    textSize: detailTextSize,
    /** Shorthand class getters for the most common patterns */
    rowPx: APPLE_ROW_PX[density],
    rowPy: APPLE_ROW_PY[density],
    rowFirst: APPLE_ROW_FIRST[density],
    rowGap: APPLE_ROW_GAP[density],
    dividerMx: APPLE_DIVIDER_MX[density],
    rowLabel: TEXT_ROW_LABEL[detailTextSize],
    sectionGap: SECTION_GAP[density],
    cardPadding: CARD_PADDING[density],
  };
}
